// Second, independent theme axis alongside DarkModeSwitcher: this one picks
// the ACCENT PALETTE (blue vs. teal) rather than light/dark. Sets/removes
// `data-vi-palette="teal"` on <html> (absence = blue, the default), which
// theme-tokens.css's four value blocks key off, combined independently with
// the `dark` class DarkModeSwitcher already owns.
//
// Same track/knob mechanic and sizing as DarkModeSwitcher for a matched pair
// in the sidebar, but the "state" here is a hue rather than a boolean
// light/dark condition: two fixed reference dots at each end of the track
// show literally what selecting that side looks like (blue's accent hex on
// the left, teal's on the right — intentionally NOT var(--vi-accent), since
// they each need to keep showing their own color regardless of which one is
// currently active), and the knob itself is filled with the CURRENTLY
// active var(--vi-accent) rather than a plain white/icon knob, so the knob
// always reads as "this is the color you have right now."
import type { PaletteName } from "../hooks/usePalette";

const PALETTE_SWATCH: Record<PaletteName, string> = {
  blue: "#5b9df2",
  teal: "#2ee6a6",
};

const PaletteSwitcher = ({
  palette,
  togglePalette,
}: {
  palette: PaletteName;
  togglePalette: () => void;
}) => (
  <button
    type="button"
    onClick={togglePalette}
    aria-label="Switch color palette"
    aria-pressed={palette === "teal"}
    title={palette === "teal" ? "Teal palette" : "Blue palette"}
    className="relative flex items-center shrink-0 w-14 h-7 rounded-full p-[3px] border transition-colors duration-300"
    style={{
      background: "var(--vi-surface)",
      borderColor: "var(--vi-card-border)",
    }}
  >
    <span
      aria-hidden="true"
      className="absolute left-[6px] w-2.5 h-2.5 rounded-full"
      style={{ background: PALETTE_SWATCH.blue }}
    />
    <span
      aria-hidden="true"
      className="absolute right-[6px] w-2.5 h-2.5 rounded-full"
      style={{ background: PALETTE_SWATCH.teal }}
    />
    <span
      aria-hidden="true"
      className={`relative z-10 w-5 h-5 rounded-full shadow-md transition-transform duration-300 ease-out ${
        palette === "teal" ? "translate-x-7" : "translate-x-0"
      }`}
      style={{ background: "var(--vi-accent)" }}
    />
  </button>
);

export default PaletteSwitcher;
